// src/pages/OrderOnTheWay/hooks/useOrderGroups.js
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';

export const useOrderGroups = (operatorId = null) => {
  const [pendingGroups, setPendingGroups] = useState({});
  const [onwayGroups, setOnwayGroups] = useState({});
  const [deliveredGroups, setDeliveredGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    // Scope strictly to this operator; Firestore throws if a where() value is
    // undefined, so wait until the operator is available.
    if (!operatorId) {
      setPendingGroups({});
      setOnwayGroups({});
      setDeliveredGroups({});
      return undefined;
    }

    const orderListRef = collection(db, 'orderList');
    const q = query(orderListRef,
      where('operatorId', '==', operatorId),
      where('orderStatus', 'in', [
        'paymentDispatchedtoOperator', 
        'paymentDispatchedToSeller',
        'paymentDispatchedtoSeller',
        'paymentDeliveredToSeller',
        'paymentDeliveredtoSeller',
        'PaymentSettled',
        'paymentSettled'
      ]));
    
    const unsubscribeOrders = onSnapshot(q, (snapshot) => {
      const ordersData = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        ordersData.push({
          docId: docSnap.id,
          ...data,
        });
      });

      // Group orders by status and seller
      const pending = {};
      const onway = {};
      const delivered = {};

      ordersData.forEach((order) => {
        const seller = order.sellerId || 'Unknown Seller';
        const groupKey = seller;
        const settlementId = order.sellerSettlementId || order.settlementId || null;
        const sellerMobile = order.sellerMobile || order.mobile || null;
        // Named distinctly from the hook's `operatorId` parameter so it does not
        // shadow the scoping filter.
        const orderOperatorId = order.operatorId || null;

        const isPending = order.orderStatus === 'paymentDispatchedtoOperator';
        const isOnWay = order.orderStatus === 'paymentDispatchedtoSeller' || order.orderStatus === 'paymentDispatchedToSeller';
        const isDelivered = 
          order.orderStatus === 'paymentDeliveredToSeller' || 
          order.orderStatus === 'paymentDeliveredtoSeller' ||
          order.orderStatus === 'PaymentSettled' ||
          order.orderStatus === 'paymentSettled';

        if (isPending) {
          if (!pending[groupKey]) {
            pending[groupKey] = {
              seller,
              orders: [],
              jreId: order.jreId || '',
              operatorId: orderOperatorId,
              sellerMobile,
              settlementId,
              sellerSettlementId: settlementId,
            };
          }
          if (!pending[groupKey].sellerMobile && sellerMobile) {
            pending[groupKey].sellerMobile = sellerMobile;
          }
          if (!pending[groupKey].settlementId && settlementId) {
            pending[groupKey].settlementId = settlementId;
            pending[groupKey].sellerSettlementId = settlementId;
          }
          pending[groupKey].orders.push(order);
        } else if (isOnWay) {
          if (!onway[groupKey]) {
            onway[groupKey] = {
              seller,
              orders: [],
              jreId: order.jreId || '',
              operatorId: orderOperatorId,
              sellerMobile,
              settlementId,
              sellerSettlementId: settlementId,
            };
          }
          if (!onway[groupKey].settlementId && settlementId) {
            onway[groupKey].settlementId = settlementId;
            onway[groupKey].sellerSettlementId = settlementId;
          }
          if (!onway[groupKey].sellerMobile && sellerMobile) {
            onway[groupKey].sellerMobile = sellerMobile;
          }
          onway[groupKey].orders.push(order);
        } else if (isDelivered) {
          if (!delivered[groupKey]) {
            delivered[groupKey] = {
              seller,
              orders: [],
              jreId: order.jreId || '',
              operatorId: orderOperatorId,
              sellerMobile,
              settlementId,
              sellerSettlementId: settlementId,
            };
          }
          if (!delivered[groupKey].settlementId && settlementId) {
            delivered[groupKey].settlementId = settlementId;
            delivered[groupKey].sellerSettlementId = settlementId;
          }
          delivered[groupKey].orders.push(order);
        }
      });
      setPendingGroups(pending);
      setOnwayGroups(onway);
      setDeliveredGroups(delivered);
    });

    return () => unsubscribeOrders();
  }, [operatorId]);

  return {
    pendingGroups,
    onwayGroups,
    deliveredGroups,
    loading,
    alertMessage,
    setAlertMessage,
    setLoading
  };
};